// eslint-disable-next-line no-unused-vars
import { Colors } from '../types';

const primary = '#143059';
const primary80 = '#43597A';
const primary60 = '#72839B';
const primary40 = '#A1ACBD';
const primary30 = '#B0B9C5';
const primary20 = '#D0D6DE';
const primary10 = '#E8EAEE';

const accent = '#FBA919';
const accent80 = '#FCBB47';
const accent60 = '#FCCC75';
const accent40 = '#FDDDA3';
const accent20 = '#FEEED1';
const accent10 = '#FFF6E8';

const secondary = '#2F6B9A';
const secondary80 = '#5989AE';
const secondary60 = '#82A6C2';
const secondary40 = '#ACC4D7';
const secondary20 = '#D5E1EB';
const secondary10 = '#EAF0F5';

const complementaryYellow = '#AA7003';
const complementaryOrange = '#F37021';
const complementaryOrange80 = '#F58C4D';
const complementaryOrange60 = '#F8A97A';
const complementaryOrange40 = '#FAC6A6';
const complementaryOrange20 = '#FDE2D3';
const complementaryOrange10 = '#FEF1E9';

const complementaryRed = '#BA3038';
const complementaryRed80 = '#C85960';
const complementaryRed60 = '#D68388';
const complementaryRed40 = '#E3ACAF';
const complementaryRed20 = '#F1D6D7';
const complementaryRed10 = '#F8EAEB';

const complementaryPurple = '#543D80';
const complementaryPurple80 = '#766499';
const complementaryPurple60 = '#988BB3';
const complementaryPurple40 = '#BBB1CC';
const complementaryPurple20 = '#DDD8E6';
const complementaryPurple10 = '#EEECF2';

const complementaryGreen = '#28863E';
const complementaryGreen80 = '#539E65';
const complementaryGreen60 = '#7EB68B';
const complementaryGreen40 = '#A9CFB2';
const complementaryGreen20 = '#D4E7D8';
const complementaryGreen10 = '#EAF3EC';

const darkGrey = '#241F21';
const darkGrey80 = '#504C4D';
const darkGrey60 = '#7C797A';
const darkGrey40 = '#A7A5A6';
const darkGrey20 = '#D3D2D3';
const darkGrey10 = '#E9E9E9';

const grey20 = '#2F3638';

const lightGrey = '#E3DDD8';
const lightGrey80 = '#E9E4E0';
const lightGrey60 = '#EEEBE8';
const lightGrey40 = '#F4F1EF';
const lightGrey20 = '#F9F8F7';
const lightGrey10 = '#FCFCFB';

const white = '#FFFFFF';
const text = '#333333';

const twitter = '#1DA1F2';
const facebook = '#1877f2';
const google = '#ea4335';
const linkedin = '#0a66c2';
const youtube = '#FF0000';
const instagram = '#d93175';
const flickr = '#1057ae';
const tumblr = '#001935';
const pinterest = '#E60023';
const tiktok = '#241F21';

const scienceBlue = '#0B57D0';

export const generalColors = {
  primary,
  primary80,
  primary60,
  primary40,
  primary30,
  primary20,
  primary10,
  accent,
  accent80,
  accent60,
  accent40,
  accent20,
  accent10,
  secondary,
  secondary80,
  secondary60,
  secondary40,
  secondary20,
  secondary10,
  complementaryYellow,
  complementaryOrange,
  complementaryOrange80,
  complementaryOrange60,
  complementaryOrange40,
  complementaryOrange20,
  complementaryOrange10,
  complementaryGreen,
  complementaryGreen80,
  complementaryGreen60,
  complementaryGreen40,
  complementaryGreen20,
  complementaryGreen10,
  complementaryPurple,
  complementaryPurple80,
  complementaryPurple60,
  complementaryPurple40,
  complementaryPurple20,
  complementaryPurple10,
  complementaryRed,
  complementaryRed80,
  complementaryRed60,
  complementaryRed40,
  complementaryRed20,
  complementaryRed10,
  darkGrey,
  darkGrey80,
  darkGrey60,
  darkGrey40,
  darkGrey20,
  darkGrey10,
  grey20,
  lightGrey,
  lightGrey80,
  lightGrey60,
  lightGrey40,
  lightGrey20,
  lightGrey10,
  white,
  text,
  scienceBlue,
};

const checkbox = {
  text: darkGrey,
  border: primary,
  background: lightGrey10,
  tick: primary,
  disabledBorder: darkGrey20,
  disabledText: darkGrey40,
  disabledBackground: lightGrey40,
  disabledTick: darkGrey40,
};

export const specificColors = {
  /**
   * Applicable to any component.
   */
  focusBorder: scienceBlue,
  focusBorderAlt: darkGrey,
  focusBorderAltInverse: lightGrey10,
  errorBorder: complementaryRed,

  button: {
    /**
     * Applicable to any button.
     */
    text: primary,
    background: primary20,
    disabledText: darkGrey80,
    disabledBackground: darkGrey20,
    activeBackground: primary40,
    focusBackground: primary30,
    hoverBackground: primary30,

    lightBackground: 'rgba(252, 252, 251, 0.6)',
    disabledLightBackground: 'rgba(211, 210, 211, 0.2)', // darkGrey20 with 20% opacity
    activeLightBackground: 'rgba(252, 252, 251, 0.9)',
    focusLightBackground: 'rgba(252, 252, 251, 0.9)',
    hoverLightBackground: 'rgba(252, 252, 251, 0.8)',

    secondary: {
      text: primary,
      background: lightGrey10,
      border: primary30,
      hoverText: primary,
      hoverBackground: primary30,
      hoverBorder: primary30,
      activeText: primary,
      activeBackground: primary40,
      focusBackground: primary30,
      disabledBorder: primary30,
      disabledBackground: darkGrey20,
    },

    outlined: {
      text: primary,
      background: lightGrey10,
      border: primary30,
      hoverText: primary,
      disabledBackground: darkGrey20,
      activeBackground: accent80,
      focusBackground: accent60,
      hoverBackground: accent60,
      hoverBorder: accent60,
      disabledText: primary,
      disabledBorder: darkGrey20,
    },

    onlyText: {
      text: darkGrey,
      hoverText: darkGrey,
      background: lightGrey10,
      disabledBackground: accent60,
      hoverBackground: accent60,
      activeBackground: accent80,
      focusBackground: accent60,
      disabledText: primary,
    },

    cta: {
      background: accent,
      disabledBackground: darkGrey20,
      activeBackground: accent80,
      focusBackground: accent60,
      hoverBackground: accent60,
    },
  },

  icon: {
    success: complementaryGreen,
    error: complementaryRed,
  },

  input: {
    text: darkGrey,
    background: lightGrey10,
    border: darkGrey60,
    disabledText: darkGrey80,
    disabledBackground: lightGrey40,
    disabledBorder: darkGrey20,
    errorBackground: lightGrey10,
    placeholderText: darkGrey80,
  },

  portfolioNav: {
    background: lightGrey60,
  },

  masthead: {
    background: lightGrey20,
  },

  modal: {
    background: lightGrey10,
    footerBackground: lightGrey20,
  },

  popover: {
    background: lightGrey10,
    border: complementaryPurple,
    success: complementaryGreen,
    error: complementaryRed,
  },

  overlay: {
    background: 'rgba(36, 31, 33, 0.75)', // darkGrey with 75% opacity
  },

  toast: {
    error: {
      background: complementaryRed10,
      iconBackground: complementaryRed40,
      icon: complementaryRed,
    },
    warning: {
      background: accent10,
      iconBackground: accent40,
      icon: complementaryYellow,
    },
    success: {
      background: complementaryGreen10,
      iconBackground: complementaryGreen40,
      icon: complementaryGreen,
    },
    info: {
      background: secondary10,
      iconBackground: secondary40,
      icon: secondary,
    },
  },

  dropdownMenu: {
    border: complementaryPurple,
    background: lightGrey10,
  },

  menuItem: {
    text: darkGrey,
    disabledText: darkGrey40,
    activeBackground: accent40,
    hoverBackground: accent10,
    focusBackground: accent10,
  },

  tooltip: {
    text: lightGrey10,
    background: darkGrey,
  },

  ribbon: {
    background: generalColors.complementaryOrange,
  },

  hyperlink: {
    color: secondary,
    hoverColor: secondary,
    alt: {
      color: secondary,
      hoverColor: secondary,
    },
  },

  tab: {
    selectedBorder: primary,
    hoverBorder: primary60,
  },

  checkbox,
  radioButton: checkbox,

  toggleSwitch: {
    onBackground: complementaryPurple,
    offBackground: darkGrey60,
  },

  tag: {
    // aka selection pill
    text: primary,
    background: complementaryOrange20,
    disabledText: primary40,
    disabledBackground: complementaryOrange10,
    activeBackground: complementaryOrange60,
    focusBackground: complementaryOrange40,
    hoverBackground: complementaryOrange40,
  },

  card: {
    background: lightGrey10,
    selectedBackground: accent10,
  },

  steppedProgressBar: {
    background: primary40,
    foreground: complementaryPurple,
  },

  genericBox: {
    background: '',
  },

  socialMedia: {
    twitter,
    facebook,
    google,
    linkedin,
    youtube,
    instagram,
    flickr,
    tumblr,
    pinterest,
    tiktok,
  },

  text: {
    success: complementaryGreen,
    error: complementaryRed,
  },

  banner: {
    success: {
      background: complementaryGreen,
    },
  },
};

export const colors: Colors = {
  ...generalColors,
  ...specificColors,
};
